import BigNumber from "bignumber.js";

/**
 * BigValueType 定义 BigMath 接受的所有数值类型
 */
export type BigValueType = string | number | BigNumber | null | undefined;

/**
 * Math 工具类 - 使用 bignumber.js 进行精确运算
 * 提供加、减、乘、除操作和精确位数控制
 */
class BigMath {
    private static defaultDP = 18; // 默认精确位数
    private static readonly ZERO = new BigNumber( 0 );

    /**
     * 内部统一转换方法，处理 null/undefined 并确保返回 BigNumber 实例
     */
    private static toBN( value: BigValueType ): BigNumber {
        if ( value === null || value === undefined ) return BigMath.ZERO;
        const bn = new BigNumber( value );
        return bn.isNaN() ? BigMath.ZERO : bn;
    }

    /**
     * 加法运算
     * @param a 被加数
     * @param b 加数
     * @param decimalPlaces 结果精确位数，默认 18
     * @returns 加法结果 (number)
     */
    static plus(
        a: BigValueType,
        b: BigValueType,
        decimalPlaces: number = BigMath.defaultDP,
    ): number {
        return BigMath.toBN( a )
            .plus( BigMath.toBN( b ) )
            .decimalPlaces( decimalPlaces, BigNumber.ROUND_DOWN )
            .toNumber();
    }

    /**
     * 减法运算
     * @param a 被减数
     * @param b 减数
     * @param decimalPlaces 结果精确位数，默认 18
     * @returns 减法结果 (number)
     */
    static minus(
        a: BigValueType,
        b: BigValueType,
        decimalPlaces: number = BigMath.defaultDP,
    ): number {
        return BigMath.toBN( a )
            .minus( BigMath.toBN( b ) )
            .decimalPlaces( decimalPlaces, BigNumber.ROUND_DOWN )
            .toNumber();
    }

    /**
     * 乘法运算
     * @param a 被乘数
     * @param b 乘数
     * @param decimalPlaces 结果精确位数，默认 18
     * @returns 乘法结果 (number)
     */
    static times(
        a: BigValueType,
        b: BigValueType,
        decimalPlaces: number = BigMath.defaultDP,
    ): number {
        return BigMath.toBN( a )
            .times( BigMath.toBN( b ) )
            .decimalPlaces( decimalPlaces, BigNumber.ROUND_DOWN )
            .toNumber();
    }

    /**
     * 除法运算
     * @param a 被除数
     * @param b 除数
     * @param decimalPlaces 结果精确位数，默认 18
     * @returns 除法结果 (number)
     */
    static div(
        a: BigValueType,
        b: BigValueType,
        decimalPlaces: number = BigMath.defaultDP,
    ): number {
        const bnB = BigMath.toBN( b );
        if ( bnB.isZero() ) return 0;
        return BigMath.toBN( a )
            .dividedBy( bnB )
            .decimalPlaces( decimalPlaces, BigNumber.ROUND_DOWN )
            .toNumber();
    }

    /**
     * 设置精确位数（向下取整）
     * @param value 要处理的值
     * @param decimalPlaces 精确位数
     * @returns 处理后的值 (number)
     */
    static dp(
        value: BigValueType,
        decimalPlaces: number,
    ): number {
        return BigMath.toBN( value )
            .decimalPlaces( decimalPlaces, BigNumber.ROUND_DOWN )
            .toNumber();
    }

    /**
     * 获取绝对值
     * @param value 要处理的值
     * @param decimalPlaces 精确位数，默认 18
     * @returns 绝对值 (number)
     */
    static abs(
        value: BigValueType,
        decimalPlaces: number = BigMath.defaultDP,
    ): number {
        return BigMath.toBN( value )
            .absoluteValue()
            .decimalPlaces( decimalPlaces, BigNumber.ROUND_DOWN )
            .toNumber();
    }

    /**
     * 取整（向下）
     * @param value 要处理的值
     * @returns 取整后的值 (number)
     */
    static floor( value: BigValueType ): number {
        return BigMath.toBN( value )
            .integerValue( BigNumber.ROUND_DOWN )
            .toNumber();
    }

    /**
     * 取整（向上）
     * @param value 要处理的值
     * @returns 取整后的值 (number)
     */
    static ceil( value: BigValueType ): number {
        return BigMath.toBN( value ).integerValue( BigNumber.ROUND_UP ).toNumber();
    }

    /**
     * 舍入到最近整数（向下取整）
     * @param value 要处理的值
     * @returns 取整后的值 (number)
     */
    static round( value: BigValueType ): number {
        return BigMath.toBN( value )
            .integerValue( BigNumber.ROUND_DOWN )
            .toNumber();
    }

    // --- 比较运算 ---

    static eq( a: BigValueType, b: BigValueType ): boolean {
        return BigMath.toBN( a ).isEqualTo( BigMath.toBN( b ) );
    }

    static gt( a: BigValueType, b: BigValueType ): boolean {
        return BigMath.toBN( a ).isGreaterThan( BigMath.toBN( b ) );
    }

    static gte( a: BigValueType, b: BigValueType ): boolean {
        return BigMath.toBN( a ).isGreaterThanOrEqualTo( BigMath.toBN( b ) );
    }

    static lt( a: BigValueType, b: BigValueType ): boolean {
        return BigMath.toBN( a ).isLessThan( BigMath.toBN( b ) );
    }

    static lte( a: BigValueType, b: BigValueType ): boolean {
        return BigMath.toBN( a ).isLessThanOrEqualTo( BigMath.toBN( b ) );
    }

    static isZero( value: BigValueType ): boolean {
        return BigMath.toBN( value ).isZero();
    }

    // --- 格式化方法 ---

    /**
     * 转换为固定小数位数的字符串 (向下取整)
     */
    static toFixed( value: BigValueType, dp: number = BigMath.defaultDP ): string {
        return BigMath.toBN( value ).toFixed( dp, BigNumber.ROUND_DOWN );
    }

    /**
     * 转换为带千分位的格式化字符串 (向下取整)
     */
    static toFormat( value: BigValueType, dp: number = 2 ): string {
        return BigMath.toBN( value ).toFormat( dp, BigNumber.ROUND_DOWN );
    }

    // --- 包装器 ---

    /**
     * 创建一个 BigNumber 实例，用于更复杂的链式运算
     */
    static BN( value: BigValueType ): BigNumber {
        return BigMath.toBN( value );
    }

    // --- 设置 ---

    /**
     * 设置默认精确位数
     * @param dp 精确位数
     */
    static setDefaultDP( dp: number ): void {
        BigMath.defaultDP = dp;
    }

    /**
     * 获取默认精确位数
     * @returns 默认精确位数
     */
    static getDefaultDP(): number {
        return BigMath.defaultDP;
    }
}

export { BigMath };
